import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  TextField,
  Alert,
  CircularProgress,
  Grid,
  Card,
  CardContent,
  Chip,
  InputAdornment,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  Business,
  Link as LinkIcon,
  ContentCopy,
  Warning,
  CheckCircle,
} from '@mui/icons-material';
import { apiService } from '../../services/apiService';
import { useAuth } from '../../context/AuthContext';
import { buildTenantUrl, getHostTenantSlug } from '../../utils/tenantResolver';
import {
  getManageAccountCacheKey,
  readManageAccountCache,
  writeManageAccountCache,
} from './manageAccountSessionCache';

type OrganizationCache = {
  organization: any;
  subscriptionInfo: {
    planType?: string;
    displayPlan?: string;
    status?: string;
    subscriptionStatus?: string;
  } | null;
  formData: { name: string; slug: string };
};

function resolveOrganizationStatusChip(
  organization: { status?: string } | null | undefined,
  subscriptionInfo: {
    displayPlan?: string;
    status?: string;
    subscriptionStatus?: string;
  } | null | undefined
): { label: string; backgroundColor: string; color: string } {
  // Org Status is usability of the organization, not plan cancel state.
  // Cancelled subscriptions still show under Plan; Status stays Active until access ends.
  const disp = (subscriptionInfo?.displayPlan || '').toUpperCase();
  const rawStatus = (subscriptionInfo?.status || subscriptionInfo?.subscriptionStatus || '').toUpperCase();

  if (disp === 'EXPIRED' || rawStatus === 'INACTIVE') {
    return { label: 'Expired', backgroundColor: '#f44336', color: 'white' };
  }

  const orgStatus = (organization?.status || 'ACTIVE').toUpperCase();
  if (orgStatus === 'ACTIVE') {
    return { label: 'Active', backgroundColor: '#4caf50', color: 'white' };
  }
  return { label: orgStatus, backgroundColor: '#9e9e9e', color: 'white' };
}

const OrganizationSettings: React.FC = () => {
  const { user } = useAuth();
  const hostTenantSlug = getHostTenantSlug();
  const organizationCacheKey = getManageAccountCacheKey(user?.id, hostTenantSlug);
  const cachedOrganization = readManageAccountCache<OrganizationCache>(
    'organization',
    organizationCacheKey
  );
  const [loading, setLoading] = useState(!cachedOrganization);
  const [error, setError] = useState<string | null>(null);

  const [organization, setOrganization] = useState<any>(cachedOrganization?.organization ?? null);
  const [subscriptionInfo, setSubscriptionInfo] = useState<{
    planType?: string;
    displayPlan?: string;
    status?: string;
    subscriptionStatus?: string;
  } | null>(cachedOrganization?.subscriptionInfo ?? null);
  const [slugCopied, setSlugCopied] = useState(false);
  const [urlCopied, setUrlCopied] = useState(false);
  const [formData, setFormData] = useState(
    cachedOrganization?.formData ?? {
      name: '',
      slug: '',
    }
  );

  useEffect(() => {
    loadOrganization();
  }, [user?.id, hostTenantSlug]);

  // Auto-hide error message after 10 seconds
  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => {
        setError(null);
      }, 10000); // 10 seconds

      return () => clearTimeout(timer);
    }
  }, [error]);

  useEffect(() => {
    if (!slugCopied) return;
    const timer = setTimeout(() => setSlugCopied(false), 2000);
    return () => clearTimeout(timer);
  }, [slugCopied]);

  useEffect(() => {
    if (!urlCopied) return;
    const timer = setTimeout(() => setUrlCopied(false), 2000);
    return () => clearTimeout(timer);
  }, [urlCopied]);

  const loadOrganization = async () => {
    const hasCache = !!readManageAccountCache<OrganizationCache>('organization', organizationCacheKey);
    try {
      if (!hasCache) {
        setLoading(true);
      }
      setError(null);

      let response: any;
      const normalizedHostSlug = hostTenantSlug?.trim().toLowerCase() || '';
      const matchesHostSlug = (o: { slug?: string }) =>
        String(o?.slug || '').trim().toLowerCase() === normalizedHostSlug;

      const subscriptionPromise = apiService.getSubscription().catch(() => null);

      if (normalizedHostSlug) {
        const [orgsResult, primaryResult] = await Promise.allSettled([
          apiService.getOrganizations(),
          apiService.getPrimaryOrganization(),
        ]);

        const orgs = orgsResult.status === 'fulfilled' ? orgsResult.value : [];
        const match = Array.isArray(orgs) ? orgs.find(matchesHostSlug) : undefined;
        if (match) {
          response = match;
        } else {
          const primary =
            primaryResult.status === 'fulfilled' ? primaryResult.value : null;
          if (primary && matchesHostSlug(primary)) {
            response = primary;
          }
        }

        if (!response) {
          setError('You do not have access to this organization workspace.');
          setFormData({ name: '', slug: normalizedHostSlug });
          return;
        }
      } else {
        response = await apiService.getPrimaryOrganization();
      }

      const nextFormData = {
        name: response.name || '',
        slug: response.slug || normalizedHostSlug || '',
      };
      setOrganization(response);
      setFormData(nextFormData);

      const subscription = await subscriptionPromise;
      let nextSubscriptionInfo: OrganizationCache['subscriptionInfo'] = null;
      if (subscription && !(subscription as { error?: string }).error) {
        nextSubscriptionInfo = {
          planType: subscription.planType,
          displayPlan: subscription.displayPlan,
          status: subscription.status,
          subscriptionStatus: subscription.subscriptionStatus,
        };
        setSubscriptionInfo(nextSubscriptionInfo);
      } else {
        setSubscriptionInfo(null);
      }

      writeManageAccountCache('organization', organizationCacheKey, {
        organization: response,
        subscriptionInfo: nextSubscriptionInfo,
        formData: nextFormData,
      });
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to load organization details');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field: string, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    setError(null);
  };

  const organizationUrl = hostTenantSlug
    ? buildTenantUrl(hostTenantSlug)
    : formData.slug
      ? buildTenantUrl(formData.slug)
      : '';

  const handleCopySlugToClipboard = () => {
    const slug = formData.slug;
    if (!slug) return;
    try {
      if (navigator && navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(slug);
        setSlugCopied(true);
      }
    } catch {
      // Ignore clipboard errors to avoid breaking the page
    }
  };

  const handleCopyUrlToClipboard = () => {
    if (!organizationUrl) return;
    try {
      if (navigator && navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(organizationUrl);
        setUrlCopied(true);
      }
    } catch {
      // Ignore clipboard errors to avoid breaking the page
    }
  };

  const validateSlug = (slug: string): boolean => {
    // Slug should be lowercase, alphanumeric with hyphens, 3-30 characters
    const slugRegex = /^[a-z0-9-]{3,30}$/;
    return slugRegex.test(slug);
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: 320,
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        maxWidth: 1200,
        mx: 'auto',
        px: { xs: 2, md: 3 },
        py: { xs: 2, md: 3 },
      }}
    >
      <Typography
        variant="h6"
        sx={{
          mb: 3,
          fontWeight: 600,
        }}
      >
        Organization Settings
      </Typography>

      {error && (
        <Alert
          severity="error"
          sx={{
            mb: 3,
          }}
          onClose={() => setError(null)}
        >
          {error}
        </Alert>
      )}

      <Grid
        container
        spacing={3}
        alignItems="stretch"
      >
        {/* Organization Information */}
        <Grid item xs={12} md={8}>
          <Card
            elevation={0}
            sx={{
              height: '100%',
              borderRadius: 2,
              border: '1px solid',
              borderColor: 'divider',
            }}
          >
            <CardContent sx={{ p: { xs: 2.5, md: 3 } }}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                  mb: 3,
                }}
              >
                <Business color="primary" fontSize="small" />
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                  Organization Information
                </Typography>
              </Box>

              <Grid container spacing={2.5}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Organization Name"
                    value={formData.name}
                    disabled
                    required
                    sx={{
                      '& .MuiInputBase-input.Mui-disabled': {
                        WebkitTextFillColor: '#000000',
                        fontWeight: 600,
                        opacity: 1,
                      },
                    }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Organization Slug"
                    value={formData.slug}
                    disabled
                    sx={{
                      '& .MuiInputBase-input.Mui-disabled': {
                        WebkitTextFillColor: '#000000',
                        fontWeight: 600,
                        opacity: 1,
                      },
                      '& .MuiInputAdornment-root svg': {
                        cursor: formData.slug ? 'pointer' : 'default',
                      },
                    }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <LinkIcon
                            onClick={formData.slug ? handleCopySlugToClipboard : undefined}
                          />
                        </InputAdornment>
                      ),
                    }}
                  />
                  {formData.slug && (
                    <Box
                      sx={{
                        mt: 0.75,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.5,
                      }}
                    >
                      <Typography variant="caption" color="text.secondary" sx={{ flex: 1 }}>
                        Your organization URL: {organizationUrl}
                      </Typography>
                      <Tooltip title="Copy organization URL">
                        <IconButton
                          size="small"
                          aria-label="Copy organization URL"
                          onClick={handleCopyUrlToClipboard}
                          sx={{ p: 0.25 }}
                        >
                          <ContentCopy sx={{ fontSize: 16 }} />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  )}
                  {slugCopied && (
                    <Typography
                      variant="caption"
                      color="success.main"
                      sx={{
                        mt: 0.5,
                        display: 'block',
                      }}
                    >
                      Organization slug copied to clipboard
                    </Typography>
                  )}
                  {urlCopied && (
                    <Typography
                      variant="caption"
                      color="success.main"
                      sx={{
                        mt: 0.5,
                        display: 'block',
                      }}
                    >
                      Organization URL copied to clipboard
                    </Typography>
                  )}
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Organization Status */}
        <Grid item xs={12} md={4}>
          <Card
            elevation={0}
            sx={{
              height: '100%',
              borderRadius: 2,
              border: '1px solid',
              borderColor: 'divider',
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <CardContent sx={{ p: { xs: 2.5, md: 3 }, flexGrow: 1 }}>
              <Typography
                variant="subtitle1"
                sx={{
                  mb: 2.5,
                  fontWeight: 600,
                }}
              >
                Organization Status
              </Typography>
              {organization && (() => {
                const statusChip = resolveOrganizationStatusChip(organization, subscriptionInfo);
                return (
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 2,
                  }}
                >
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      Status
                    </Typography>
                    <Chip
                      label={statusChip.label}
                      size="small"
                      sx={{
                        mt: 0.75,
                        alignSelf: 'flex-start',
                        backgroundColor: statusChip.backgroundColor,
                        color: statusChip.color,
                        fontWeight: 600,
                      }}
                    />
                  </Box>
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      Plan
                    </Typography>
                    <Typography variant="body1" sx={{ mt: 0.75, fontWeight: 500 }}>
                      {subscriptionInfo?.displayPlan ||
                        subscriptionInfo?.planType ||
                        organization.displayPlan ||
                        organization.planType ||
                        'FREE'}
                    </Typography>
                  </Box>
                </Box>
                );
              })()}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default OrganizationSettings;

